import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RTButton from '../common/RTButton';
import Table from '../common/Table';
import RTFilters from '../common/RTFilters';
import RTFilteredTable from '../common/RTFilteredTable';
import RTSpinner from '../common/RTSpinner';
import AddPayment from './AddPayment';
import SendReminderModal from './SendReminderModal';
import { apiGet, apiPost } from '../../api/apiClient';
import {
  toastSuccess,
  toastError,
  toastConfirm,
  toastWarning,
} from '../common/toast';
import { OCCUPANCY_GENDER_FILTER_OPTIONS } from '../../utils/occupancyConstants';
import '../common/rtCommon.css';
import './ViewUnpaidInvoices.css';
import RTPageLayout from '../common/RTPageLayout';
import RTStatusPill, { formatStatusLabel } from '../common/statusPill';

const UNPAID_INVOICE_MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const PAYMENT_LINK_STATUS_LABELS = {
  active: 'Active',
  expired: 'Expired',
  cancelled: 'Cancelled',
  completed: 'Completed',
  not_generated: 'Not Generated',
};

const PAYMENT_LINK_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'completed', label: 'Completed' },
  { value: 'not_generated', label: 'Not Generated' },
];

const getUnpaidInvoiceMonthOptions = () => [
  { value: '', label: 'All Months' },
  ...UNPAID_INVOICE_MONTH_NAMES.map((label, index) => ({
    value: index + 1,
    label,
  })),
];

const getUnpaidInvoiceYearOptions = () => {
  const currentYear = new Date().getFullYear();
  return [
    { value: '', label: 'All Years' },
    ...Array.from({ length: 3 }, (_, index) => {
      const year = currentYear - index;
      return { value: year, label: String(year) };
    }),
  ];
};

const ViewUnpaidInvoices = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [checkingPaymentStatus, setCheckingPaymentStatus] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] =
    useState(null);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [buildingOptions, setBuildingOptions] = useState([]);
  const [buildingSearch, setBuildingSearch] = useState('');
  const [filters, setFilters] = useState({
    month: undefined,
    year: undefined,
    showOverdueOnly: false,
    search: '',
    paymentLinkStatus: '',
    building_id: undefined,
    occupancy_gender: undefined,
  });
  const [appliedFilters, setAppliedFilters] = useState({
    month: undefined,
    year: undefined,
    showOverdueOnly: false,
    search: '',
    paymentLinkStatus: '',
    building_id: undefined,
    occupancy_gender: undefined,
  });

  useEffect(() => {
    loadUnpaidInvoices(appliedFilters);
    loadBuildingOptions();
  }, []);

  const loadBuildingOptions = async () => {
    try {
      const response = await apiGet('/building/names?status=Active');
      setBuildingOptions(
        (response.data || []).map((b) => ({
          value: String(b.building_id),
          label: b.name,
        })),
      );
    } catch (error) {
      console.error('Error loading buildings:', error);
    }
  };

  const loadUnpaidInvoices = async (
    filterParams = appliedFilters,
    checkPaymentStatus = false,
  ) => {
    try {
      if (checkPaymentStatus) {
        setCheckingPaymentStatus(true);
      } else {
        setLoading(true);
      }

      // Build query parameters
      const params = new URLSearchParams();
      params.append('limit', '1000');
      params.append('order_by', 'building');

      // Add month/year filters if provided
      if (filterParams.month !== undefined && filterParams.year !== undefined) {
        // Both month and year selected: filter to specific month
        const startDate = `${filterParams.year}-${String(filterParams.month).padStart(2, '0')}-01`;
        const lastDay = new Date(
          filterParams.year,
          filterParams.month,
          0,
        ).getDate();
        const endDate = `${filterParams.year}-${String(filterParams.month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      } else if (filterParams.year !== undefined) {
        // Only year selected: filter to entire year
        const startDate = `${filterParams.year}-01-01`;
        const endDate = `${filterParams.year}-12-31`;
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      } else if (filterParams.month !== undefined) {
        // Only month selected: use current year with selected month
        const currentYear = new Date().getFullYear();
        const startDate = `${currentYear}-${String(filterParams.month).padStart(2, '0')}-01`;
        const lastDay = new Date(currentYear, filterParams.month, 0).getDate();
        const endDate = `${currentYear}-${String(filterParams.month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      }
      // If neither month nor year selected, no date filter is applied (loads all)

      // Add unified search filter
      if (filterParams.search && filterParams.search.trim()) {
        params.append('search', filterParams.search.trim());
      }

      if (filterParams.occupancy_gender) {
        params.append('occupancy_gender', filterParams.occupancy_gender);
      }

      // Add payment link status filter
      if (
        filterParams.paymentLinkStatus &&
        filterParams.paymentLinkStatus.trim()
      ) {
        params.append(
          'payment_link_status',
          filterParams.paymentLinkStatus.trim(),
        );
      }

      if (filterParams.building_id) {
        params.append('building_id', filterParams.building_id);
      }

      let allUnpaidInvoices = [];

      if (filterParams.showOverdueOnly) {
        // Load only overdue invoices
        params.append('status', 'overdue');
      } else {
        // Load sent, partially paid, and overdue invoices with comma-separated status
        params.append('status', 'sent,partially_paid,overdue');
      }

      // Add check_payment_status flag if requested
      if (checkPaymentStatus) {
        params.append('check_payment_status', 'true');
      }

      // Single API call with comma-separated status values
      const response = await apiGet(
        `/invoicing/invoices?${params.toString()}`,
        {
          timeout: checkPaymentStatus ? 300000 : 60000, // 5 minutes when checking payment status, 1 minute otherwise
        },
      );
      // Handle new response structure {invoices: [...], totalInvoicesAmount: number}
      const responseData = response?.data || [];
      allUnpaidInvoices = Array.isArray(responseData)
        ? responseData
        : responseData.invoices || [];

      setInvoices(allUnpaidInvoices);
    } catch (error) {
      console.error('Error loading unpaid invoices:', error);
      toastError('Failed to load unpaid invoices. Please try again.');
    } finally {
      setLoading(false);
      setCheckingPaymentStatus(false);
    }
  };

  const handleFilterApply = () => {
    setAppliedFilters(filters);
    loadUnpaidInvoices(filters);
  };

  const applyImmediateFilter = (field, rawValue) => {
    let parsed =
      rawValue === '' || rawValue === undefined ? undefined : rawValue;
    if (field === 'month' || field === 'year') {
      parsed = rawValue === '' ? undefined : parseInt(rawValue, 10);
    }
    if (field === 'building_id' && parsed === undefined) {
      setBuildingSearch('');
    }
    const newApplied = { ...appliedFilters, [field]: parsed };
    const newFilters = { ...filters, [field]: parsed };
    setFilters(newFilters);
    setAppliedFilters(newApplied);
    loadUnpaidInvoices(newApplied);
  };

  const filterFields = useMemo(
    () => [
      {
        key: 'month',
        label: 'Month',
        type: 'dropdown',
        width: 'compact',
        options: getUnpaidInvoiceMonthOptions(),
        applyImmediately: true,
      },
      {
        key: 'year',
        label: 'Year',
        type: 'dropdown',
        width: 'compact',
        options: getUnpaidInvoiceYearOptions(),
        applyImmediately: true,
      },
      {
        key: 'building_id',
        label: 'Building',
        type: 'autocomplete',
        width: 'medium',
        placeholder: 'Search building...',
        options: buildingOptions,
        applyImmediately: true,
      },
      {
        key: 'occupancy_gender',
        label: 'Gender',
        type: 'dropdown',
        width: 'compact',
        options: OCCUPANCY_GENDER_FILTER_OPTIONS,
        applyImmediately: true,
      },
      {
        key: 'search',
        label: 'Search',
        type: 'text',
        width: 'wide',
        placeholder: 'PG name, mobile, or address...',
        applyImmediately: false,
      },
      {
        key: 'paymentLinkStatus',
        label: 'Payment Link',
        type: 'dropdown',
        width: 'compact',
        options: PAYMENT_LINK_FILTER_OPTIONS,
        applyImmediately: true,
      },
      {
        key: 'showOverdueOnly',
        label: 'Show overdue only',
        type: 'checkbox',
        checkboxText: 'Show overdue only',
        applyImmediately: false,
      },
    ],
    [buildingOptions],
  );

  const handleFilterChange = (key, value, { applyImmediately }) => {
    if (applyImmediately) {
      applyImmediateFilter(key, value);
      return;
    }
    setFilters((prev) => ({
      ...prev,
      [key]:
        key === 'showOverdueOnly'
          ? Boolean(value)
          : key === 'building_id'
            ? value || undefined
            : (value ?? ''),
    }));
  };

  const handleFilterSearchChange = (key, text) => {
    if (key === 'building_id') {
      setBuildingSearch(text);
      if (!text && appliedFilters.building_id) {
        applyImmediateFilter('building_id', undefined);
      }
    }
  };

  const handleClearFilters = () => {
    const defaultFilters = {
      month: undefined,
      year: undefined,
      showOverdueOnly: false,
      search: '',
      paymentLinkStatus: '',
      building_id: undefined,
      occupancy_gender: undefined,
    };
    setFilters(defaultFilters);
    setBuildingSearch('');
    setAppliedFilters(defaultFilters);
    loadUnpaidInvoices(defaultFilters);
  };

  const appliedFilterPills = useMemo(() => {
    const pills = [];
    if (appliedFilters.month) {
      pills.push({
        key: 'month',
        label: `Month: ${UNPAID_INVOICE_MONTH_NAMES[appliedFilters.month - 1]}`,
      });
    }
    if (appliedFilters.year) {
      pills.push({ key: 'year', label: `Year: ${appliedFilters.year}` });
    }
    if (appliedFilters.building_id) {
      const buildingLabel =
        buildingSearch ||
        buildingOptions.find(
          (o) => o.value === String(appliedFilters.building_id),
        )?.label ||
        appliedFilters.building_id;
      pills.push({ key: 'building_id', label: `Building: ${buildingLabel}` });
    }
    if (appliedFilters.search?.trim()) {
      pills.push({
        key: 'search',
        label: `Search: ${appliedFilters.search.trim()}`,
      });
    }
    if (appliedFilters.occupancy_gender) {
      const genderLabel =
        OCCUPANCY_GENDER_FILTER_OPTIONS.find(
          (o) => o.value === appliedFilters.occupancy_gender,
        )?.label || appliedFilters.occupancy_gender;
      pills.push({ key: 'occupancy_gender', label: `Gender: ${genderLabel}` });
    }
    if (appliedFilters.paymentLinkStatus) {
      const statusLabel =
        PAYMENT_LINK_STATUS_LABELS[appliedFilters.paymentLinkStatus] ||
        appliedFilters.paymentLinkStatus;
      pills.push({
        key: 'paymentLinkStatus',
        label: `Payment Link: ${statusLabel}`,
      });
    }
    if (appliedFilters.showOverdueOnly) {
      pills.push({ key: 'showOverdueOnly', label: 'Overdue only' });
    }
    return pills;
  }, [appliedFilters, buildingSearch, buildingOptions]);

  const handleRemoveAppliedFilter = (key) => {
    const next = { ...appliedFilters };
    if (key === 'showOverdueOnly') {
      next.showOverdueOnly = false;
    } else if (key === 'building_id') {
      next.building_id = undefined;
      setBuildingSearch('');
    } else if (key === 'search' || key === 'paymentLinkStatus') {
      next[key] = '';
    } else {
      next[key] = undefined;
    }
    setFilters(next);
    setAppliedFilters(next);
    loadUnpaidInvoices(next);
  };

  // Generate dynamic title based on actually applied filters
  const getTableTitle = () => {
    let title = 'Unpaid Invoices';

    if (appliedFilters.showOverdueOnly) {
      title += ' - Overdue Only';
    }

    if (appliedFilters.month && appliedFilters.year) {
      const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];
      title += ` (${monthNames[appliedFilters.month - 1]} ${appliedFilters.year})`;
    } else if (appliedFilters.year) {
      title += ` (${appliedFilters.year})`;
    } else if (appliedFilters.month) {
      const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];
      title += ` (${monthNames[appliedFilters.month - 1]})`;
    } else {
      title += ` (All)`;
    }

    // Calculate total pending amount
    const totalPending = invoices.reduce(
      (sum, invoice) => sum + (invoice.amount_remaining || 0),
      0,
    );

    title += ` - ${invoices.length} invoice${invoices.length !== 1 ? 's' : ''}`;
    title += ` - Total Pending: ₹${totalPending.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    if (selectedInvoices.length > 0) {
      title += ` - ${selectedInvoices.length} selected`;
    }

    return title;
  };

  const handleSelectInvoice = (invoiceId, checked) => {
    if (checked) {
      setSelectedInvoices((prev) => [...prev, invoiceId]);
    } else {
      setSelectedInvoices((prev) => prev.filter((id) => id !== invoiceId));
      setSelectAll(false);
    }
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedInvoices(invoices.map((invoice) => invoice.invoice_id));
    } else {
      setSelectedInvoices([]);
    }
  };

  const handleViewInvoice = (invoiceId) => {
    navigate(`/payments/invoices/${invoiceId}/details`);
  };

  const handleAddPayment = async (invoiceId) => {
    try {
      // Fetch invoice details to get PG information
      const invoiceResponse = await apiGet(`/invoicing/invoices/${invoiceId}`);
      if (invoiceResponse?.data) {
        const invoice = invoiceResponse.data;

        // Fetch PG details
        const pgResponse = await apiGet(`/pgdetails/${invoice.pg_id}`);
        if (pgResponse?.data) {
          setSelectedInvoiceForPayment({
            invoice: invoice,
            pg: pgResponse.data,
          });
          setShowAddPayment(true);
        } else {
          toastError('Failed to load paying guest details');
        }
      } else {
        toastError('Failed to load invoice details');
      }
    } catch (error) {
      console.error('Error loading invoice data:', error);
      toastError('Failed to load invoice data');
    }
  };

  const handleCloseAddPayment = () => {
    setShowAddPayment(false);
    setSelectedInvoiceForPayment(null);
    // Reload invoices to reflect any payments made
    loadUnpaidInvoices(appliedFilters);
  };

  const handleGeneratePaymentLink = async (invoiceId) => {
    try {
      // Show confirmation toast
      const confirmed = await toastConfirm(
        true,
        'Are you sure you want to generate a payment link for this invoice? A WhatsApp notification will be sent to the customer.',
      );

      if (!confirmed) {
        return;
      }

      setProcessing(true);
      const response = await apiPost(
        `/invoicing/invoices/${invoiceId}/generate-payment-link`,
        {},
      );

      if (response?.data?.success) {
        // Check if WhatsApp was sent successfully
        if (response.data.whatsapp_sent) {
          toastSuccess('Payment link generated and sent via WhatsApp');
        } else if (response.data.whatsapp_error) {
          toastWarning(
            `Payment link generated but WhatsApp failed: ${response.data.whatsapp_error}`,
          );
        } else {
          toastSuccess('Payment link generated successfully');
        }
        loadUnpaidInvoices(appliedFilters); // Reload to get updated status
      } else {
        toastError(response?.data?.error || 'Failed to generate payment link');
      }
    } catch (error) {
      console.error('Error generating payment link:', error);
      const errorMsg =
        error?.response?.data?.detail ||
        'Failed to generate payment link. Please try again.';
      toastError(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelPaymentLink = async (invoiceId) => {
    // Show confirmation toast
    const confirmed = await toastConfirm(
      true,
      'Are you sure you want to cancel this payment link?',
    );

    if (!confirmed) {
      return;
    }

    try {
      setProcessing(true);
      const response = await apiPost(
        `/invoicing/invoices/${invoiceId}/cancel-payment-link`,
        {},
      );

      // apiPost returns axios response, so we need to access response.data
      const data = response.data;
      console.log('Cancel payment link response:', data);

      // Update local state immediately to reflect the change
      setInvoices((prevInvoices) =>
        prevInvoices.map((inv) =>
          inv.invoice_id === invoiceId
            ? { ...inv, payment_provider_link_status: 'Cancelled' }
            : inv,
        ),
      );

      toastSuccess(data?.message || 'Payment link cancelled successfully');
    } catch (error) {
      console.error('Error cancelling payment link:', error);
      // Error message from backend or generic message
      const errorMsg =
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to cancel payment link. Please try again.';
      toastError(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const handleSendReminders = async () => {
    try {
      setShowReminderModal(false);
      setProcessing(true);

      const response = await apiPost('/invoicing/send-reminders', {
        invoice_ids: selectedInvoices,
      });

      const data = response.data;

      if (data.success) {
        const { sent_count, skipped_count, failed_count } = data;
        let message = `Reminders processed: ${sent_count} sent`;

        if (skipped_count > 0) {
          message += `, ${skipped_count} skipped (threshold not met)`;
        }
        if (failed_count > 0) {
          message += `, ${failed_count} failed`;
        }

        toastSuccess(message);

        // Clear selection only, no need to reload as data hasn't changed
        setSelectedInvoices([]);
        setSelectAll(false);
      } else {
        toastError(data.error || 'Failed to send reminders');
      }
    } catch (error) {
      console.error('Error sending reminders:', error);
      const errorMsg =
        error?.response?.data?.detail ||
        'Failed to send reminders. Please try again.';
      toastError(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  // If AddPayment is shown, render only the AddPayment component
  if (showAddPayment && selectedInvoiceForPayment) {
    return (
      <AddPayment
        onClose={handleCloseAddPayment}
        preSelectedPG={selectedInvoiceForPayment.pg}
        preSelectedInvoiceId={selectedInvoiceForPayment.invoice.invoice_id}
      />
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatAmount = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handlePGClick = (pgId) => {
    window.open(`/paying-guest/view/${pgId}`, '_blank');
  };

  const handleBuildingClick = (buildingId) => {
    window.open(`/building/view/${buildingId}`, '_blank');
  };

  const prepareTableData = () => {
    return invoices.map((invoice) => {
      return {
        id: invoice.invoice_id,
        invoice_id: invoice.invoice_id,
        invoice_number: invoice.invoice_number,
        pg_id: invoice.pg_id || invoice.paying_guest?.pg_id,
        paying_guest: invoice.paying_guest,
        building_id: invoice.building_id || invoice.building?.id,
        building_name: invoice.building?.name || 'N/A',
        building: invoice.building,
        room_id: invoice.room_id,
        room_name: invoice.room_name || invoice.room?.room_name || 'N/A',
        items: invoice.items_text || 'No items',
        invoice_date: invoice.invoice_date,
        total_amount: invoice.total_amount,
        amount_paid: invoice.amount_paid,
        amount_remaining: invoice.amount_remaining,
        status: invoice.status,
        invoice: invoice,
      };
    });
  };

  const getStatusBadge = (status) => (
    <RTStatusPill status={status}>{formatStatusLabel(status)}</RTStatusPill>
  );

  const columns = [
    {
      Header: () => (
        <input
          type="checkbox"
          checked={selectAll}
          onChange={(e) => handleSelectAll(e.target.checked)}
          className="rounded border-gray-300"
        />
      ),
      accessor: 'select',
      disableExport: true,
      Cell: ({ row }) => (
        <input
          type="checkbox"
          checked={selectedInvoices.includes(row.invoice_id)}
          onChange={(e) =>
            handleSelectInvoice(row.invoice_id, e.target.checked)
          }
          className="rounded border-gray-300"
        />
      ),
      disableSortBy: true,
      width: 50,
      align: 'center',
    },
    {
      Header: 'Invoice #',
      accessor: 'invoice_number',
    },
    {
      Header: 'Building',
      accessor: 'building_name',
      Cell: ({ row }) =>
        row.building_id ? (
          <RTButton
            type="link"
            onClick={() => handleBuildingClick(row.building_id)}
            className="rt-link-button"
          >
            {row.building_name || 'N/A'}
          </RTButton>
        ) : (
          row.building_name || 'N/A'
        ),
      exportValue: (row) => row.building_name || 'N/A',
    },
    {
      Header: 'Room',
      accessor: 'room_name',
    },
    {
      Header: 'Paying Guest',
      accessor: 'paying_guest',
      Cell: ({ row }) =>
        row.pg_id ? (
          <RTButton
            type="link"
            onClick={() => handlePGClick(row.pg_id)}
            className="rt-link-button"
          >
            {row.paying_guest?.name || 'N/A'}
          </RTButton>
        ) : (
          row.paying_guest?.name || 'N/A'
        ),
      exportValue: (row) => row.paying_guest?.name || 'N/A',
    },
    {
      Header: 'Mobile',
      accessor: 'mobile',
      Cell: ({ row }) => row.paying_guest?.mobile || 'N/A',
      exportValue: (row) => row.paying_guest?.mobile || 'N/A',
    },
    {
      Header: 'Invoice Items',
      accessor: 'items',
    },
    {
      Header: 'Amount',
      accessor: 'total_amount',
      Cell: ({ value }) => formatAmount(value),
    },
    {
      Header: 'Remaining',
      accessor: 'amount_remaining',
      Cell: ({ value }) => formatAmount(value || 0),
    },
    {
      Header: 'Payment Link',
      accessor: 'payment_link_status',
      align: 'center',
      Cell: ({ row }) => {
        const status =
          row.invoice?.payment_provider_link_status || 'Not Generated';
        return <RTStatusPill status={status}>{status}</RTStatusPill>;
      },
    },
    {
      Header: 'Status',
      accessor: 'status',
      align: 'center',
      Cell: ({ value }) => getStatusBadge(value),
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      align: 'center',
      Cell: ({ row }) => {
        return (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <RTButton
              type="link"
              variant="primary"
              permissionKey="payments.add_payment"
              onClick={() => handleAddPayment(row.invoice_id)}
            >
              Add Payment
            </RTButton>
            <RTButton
              type="link"
              variant="primary"
              onClick={() => handleViewInvoice(row.invoice_id)}
            >
              View
            </RTButton>
          </div>
        );
      },
      disableSortBy: true,
      disableExport: true,
    },
  ];

  return (
    <RTPageLayout
      activePath={location.pathname}
      title="View Unpaid Invoices"
      actions={
        <>
          <RTButton variant="secondary" onClick={() => navigate('/payments')}>
            &#8592; Back
          </RTButton>
          {selectedInvoices.length > 0 && (
            <RTButton
              variant="warning"
              onClick={() => setShowReminderModal(true)}
            >
              <i className="bi bi-bell me-2"></i>
              Send Reminder ({selectedInvoices.length})
            </RTButton>
          )}
          <RTButton
            variant="primary"
            onClick={() => loadUnpaidInvoices(appliedFilters, true)}
            disabled={loading || checkingPaymentStatus}
          >
            {checkingPaymentStatus ? 'Checking...' : 'Check Payment Status'}
          </RTButton>
        </>
      }
    >
      {/* Payment status check info banner */}
      {checkingPaymentStatus && (
        <RTSpinner
          fullScreen={false}
          message="Checking payment link status with payment provider... This may take a few minutes depending on the number of invoices. Please wait."
        />
      )}

      <div className="rt-main-section">
        <div className="invoices-table-container">
          <RTFilteredTable
            loading={loading}
            loadingMessage="Loading unpaid invoices..."
            filters={
              <RTFilters
                fields={filterFields}
                values={filters}
                searchValues={{ building_id: buildingSearch }}
                onChange={handleFilterChange}
                onSearchChange={handleFilterSearchChange}
                showApplyButton={true}
                onApply={handleFilterApply}
                pills={appliedFilterPills}
                onRemovePill={handleRemoveAppliedFilter}
                onClearAll={handleClearFilters}
              />
            }
          >
            <Table
              columns={columns}
              data={prepareTableData()}
              title={getTableTitle()}
              noDataText="No unpaid invoices found."
              exportable={true}
              exportFileName="unpaid_invoices"
              showSearch={false}
              showPagination={true}
              pageSize={25}
              disableSort={true}
              rowGroupConfig={{
                groups: [{ key: 'room_id', columns: ['room_name'] }],
              }}
            />
          </RTFilteredTable>
        </div>
      </div>

      {/* Loading Spinner */}
      {processing && <RTSpinner fullScreen={true} />}

      {/* Send Reminder Modal */}
      <SendReminderModal
        show={showReminderModal}
        onHide={() => setShowReminderModal(false)}
        onConfirm={handleSendReminders}
        selectedCount={selectedInvoices.length}
      />
    </RTPageLayout>
  );
};

export default ViewUnpaidInvoices;
